import { useQuery } from '@apollo/client';
import {
  ICustomerAddress,
  ListEligibleShippingMethodsQueryResult,
  PreferredShippingMethod,
} from '@monofront/types';
import { useMemo } from 'react';
import { ABTEST_DELIVERY_BAR } from '../constants/featureFlags';
import { formatShippingMethod, useFeatureIsOn } from '../contexts';
import { LIST_ELIGIBLE_SHIPPING_METHODS } from '../queries/shippingMethods';
import { transformShippingMethods } from '../stateMachines/deliveryBar/formatters';
import { DeliveryBarMachineContextShippingMethod } from '../stateMachines/deliveryBar/types';
import { matchShippingMethodToEligible } from '../utils/shippingMethodMatcher';

const EMPTY_METHODS: DeliveryBarMachineContextShippingMethod[] = [];

export const useFetchEligibleShippingMethods = (
  address:
    | { city: string; country: string; postalCode: string; street: string }
    | null
    | undefined,
) => {
  const isDeliveryBarABTestEnabled = useFeatureIsOn(ABTEST_DELIVERY_BAR);
  const { country: countryCode, postalCode } = address || {};

  const { data: fetchedData } =
    useQuery<ListEligibleShippingMethodsQueryResult>(
      LIST_ELIGIBLE_SHIPPING_METHODS,
      {
        ssr: false,
        fetchPolicy: 'cache-first',
        skip:
          !isDeliveryBarABTestEnabled || !countryCode || !postalCode,
        variables: {
          shippingAddress: {
            countryCode: countryCode ?? '',
            postcode: postalCode ?? '',
          },
        },
      },
    );

  return useMemo(() => {
    if (fetchedData?.listEligibleShippingMethods) {
      return transformShippingMethods(fetchedData.listEligibleShippingMethods);
    }

    return EMPTY_METHODS;
  }, [fetchedData]);
};

/**
 * Hook that fetches and returns the customer's preferred shipping method with configurations.
 * Uses resolvedCustomer data and fetches eligible shipping methods based on address.
 * Matches the preferred method to eligible methods to get correct configurations.
 */
export const usePreferredShippingMethodWithConfigurations = (customer: {
  address: ICustomerAddress | null;
  preferredShippingMethod: PreferredShippingMethod | null;
}): DeliveryBarMachineContextShippingMethod | undefined => {
  // Transform fetched data to machine context format
  const eligibleShippingMethods = useFetchEligibleShippingMethods(
    customer?.address,
  );

  // Format and match the preferred shipping method
  const shippingMethodWithConfigurations = useMemo(() => {
    if (!customer?.preferredShippingMethod) {
      return null;
    }

    const formattedShippingMethod = formatShippingMethod(
      customer.preferredShippingMethod,
    );

    return matchShippingMethodToEligible(
      formattedShippingMethod,
      eligibleShippingMethods,
    );
  }, [customer?.preferredShippingMethod, eligibleShippingMethods]);

  return shippingMethodWithConfigurations ?? undefined;
};
